import { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, X, Play, Shield, ChevronDown, ChevronUp, ArrowRight, Clock, Users, Zap, Target, Mail, FileText, Video, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useReveal(0.1);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

function FAQ({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b" style={{ borderColor: "#E5E7EB" }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left cursor-pointer bg-transparent border-none">
        <span className="font-semibold text-sm" style={{ color: "#1F2937" }}>{question}</span>
        {open ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: "#9CA3AF" }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "#9CA3AF" }} />}
      </button>
      {open && <p className="text-sm pb-4 leading-relaxed" style={{ color: "#6B7280" }}>{answer}</p>}
    </div>
  );
}

const isForYou = [
  "You have an audience of 1,000+ on any platform",
  "You're creating content consistently but not monetising effectively",
  "You're willing to invest 2-3 hours per week for 4 weeks",
  "You want a done-with-you approach, not just another course to watch",
  "You're ready to build real revenue from your content this quarter",
];

const notForYou = [
  "You're looking for get-rich-quick schemes",
  "You have no existing audience or content",
  "You're not willing to implement what we build together",
  "You just want to \"learn\" without taking action",
];

const weeks = [
  { title: "Week 1: Strategy & AI Workflow Setup", desc: "We audit your content, audience, and goals. Then we build your custom AI content workflow so you can create 10x faster.", icon: <Target className="w-6 h-6" /> },
  { title: "Week 2: Build Your First Digital Product", desc: "We don't just teach you — we build it WITH you. Template, sales copy, pricing, all done.", icon: <FileText className="w-6 h-6" /> },
  { title: "Week 3: Sales Funnel & Email System", desc: "Your sales page, checkout, and 7-email launch sequence — built and tested.", icon: <Mail className="w-6 h-6" /> },
  { title: "Week 4: Launch & Optimise", desc: "We launch your product while I'm there to support you. Real-time feedback, troubleshooting, and optimisation.", icon: <Zap className="w-6 h-6" /> },
];

const deliverables = [
  { title: "4 x 60-minute 1-on-1 strategy calls", value: "£2,000" },
  { title: "Custom AI workflow library for your niche", value: "£500" },
  { title: "Your first digital product — BUILT", value: "£1,000" },
  { title: "Done-for-you sales page copy", value: "£750" },
  { title: "7-email launch sequence written for you", value: "£500" },
  { title: "30 days of async support via Loom & voice notes", value: "£500" },
  { title: "Full access to AI StoryCraft Studio course", value: "£79" },
];

const testimonials = [
  { name: "Daniel", role: "Tech YouTuber, 25K subs", text: "Isaac built my entire product launch system with me. I made £3,200 in the first week. Worth every penny of the investment." },
  { name: "Aisha", role: "Lifestyle Instagram, 15K followers", text: "Before the DWY programme, I had no idea how to monetise. After 4 weeks, I had a course, a sales page, and £1,800 in sales." },
  { name: "Tom", role: "Business Podcast, 8K listeners", text: "The 1-on-1 calls were game-changing. Having someone build it WITH you instead of just telling you what to do makes all the difference." },
  { name: "Nina", role: "Fitness TikToker, 40K followers", text: "I went from £0 to £4,500/month in 6 weeks. The AI workflows alone save me 15 hours a week." },
];

export default function Apply() {
  const formRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState({
    full_name: "", email: "", platform: "", audience_size: "", monthly_revenue: "", biggest_challenge: "", why_work_with_us: "", ready_to_invest: "", how_soon: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.platform || !form.audience_size || !form.monthly_revenue || !form.biggest_challenge.trim() || !form.why_work_with_us.trim() || !form.ready_to_invest || !form.how_soon) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("dwy_applications" as any).insert({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        platform: form.platform,
        audience_size: form.audience_size,
        monthly_revenue: form.monthly_revenue,
        biggest_challenge: form.biggest_challenge.trim(),
        why_work_with_us: form.why_work_with_us.trim(),
        ready_to_invest: form.ready_to_invest,
        how_soon: form.how_soon,
      } as any);
      if (error) throw error;

      // Also send notification email via edge function
      try {
        await supabase.functions.invoke("send-support-email", {
          body: {
            subject: `New DWY Application: ${form.full_name}`,
            message: `New application from ${form.full_name} (${form.email})\n\nPlatform: ${form.platform}\nAudience: ${form.audience_size}\nRevenue: ${form.monthly_revenue}\nReady to invest: ${form.ready_to_invest}\nHow soon: ${form.how_soon}\n\nBiggest challenge: ${form.biggest_challenge}\n\nWhy us: ${form.why_work_with_us}`,
            email: form.email,
            name: form.full_name,
          },
        });
      } catch (emailErr) {
        console.warn("Email notification failed:", emailErr);
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error("Application submit error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectClass = "w-full h-12 px-4 rounded-xl text-base bg-white outline-none appearance-none cursor-pointer";
  const selectStyle = { border: "1px solid #E5E7EB", color: "#1F2937" };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      <Helmet>
        <title>Apply — Done-With-You AI Content System | AI StoryCraft Studio</title>
        <meta name="description" content="Apply for the Done-With-You AI Content System. We'll build your entire monetisation system with you in 30 days." />
      </Helmet>

      {/* Hero */}
      <div className="w-full" style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}>
        <div className="mx-auto px-4 md:px-6 py-16 md:py-24 text-center" style={{ maxWidth: 820 }}>
          <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "rgba(255,255,255,0.7)", letterSpacing: "0.1em" }}>APPLICATION ONLY · LIMITED TO 5 CLIENTS PER MONTH</p>
          <h1 className="text-2xl md:text-[2.75rem] font-extrabold leading-tight mb-4 text-white">
            We'll Build Your Entire AI Monetisation System{" "}
            <span style={{ color: "#F59E0B" }}>With You</span> — In 30 Days
          </h1>
          <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: "rgba(255,255,255,0.85)" }}>
            Stop guessing. In 4 weeks, you'll have a complete content-to-revenue system: your AI workflows, your first digital product, your sales funnel, and your email sequences — all built and ready to generate income.
          </p>
          <button onClick={scrollToForm} className="font-bold text-base px-8 py-4 rounded-full border-none cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]" style={{ backgroundColor: "#FFFFFF", color: "#7C3AED" }}>
            See If You Qualify →
          </button>
          <p className="text-xs mt-3" style={{ color: "rgba(255,255,255,0.6)" }}>Only 5 spots available this month</p>
        </div>
      </div>

      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: 820 }}>
        {/* Video */}
        <Reveal>
          <div className="py-12">
            <div className="w-full rounded-2xl overflow-hidden flex items-center justify-center cursor-pointer" style={{ aspectRatio: "16/9", background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                  <Play className="w-7 h-7 text-white ml-1" />
                </div>
                <p className="text-white font-semibold text-sm">How the Done-With-You system works (5 minutes)</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Who This Is For */}
        <Reveal delay={100}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <div className="bg-white rounded-xl p-6" style={{ border: "1px solid #E5E7EB", borderTop: "4px solid #10B981" }}>
              <h3 className="font-bold text-base mb-4" style={{ color: "#1F2937" }}>This IS For You If...</h3>
              <div className="space-y-3">
                {isForYou.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#10B981" }} />
                    <span className="text-sm" style={{ color: "#374151" }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-6" style={{ border: "1px solid #E5E7EB", borderTop: "4px solid #EF4444" }}>
              <h3 className="font-bold text-base mb-4" style={{ color: "#1F2937" }}>This is NOT For You If...</h3>
              <div className="space-y-3">
                {notForYou.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <X className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#EF4444" }} />
                    <span className="text-sm" style={{ color: "#374151" }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* What's Included - 4 Weeks */}
        <Reveal delay={200}>
          <div className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-center mb-8" style={{ color: "#1F2937" }}>What's Included</h2>
            <div className="space-y-6">
              {weeks.map((week, i) => (
                <div key={i} className={`flex flex-col md:flex-row items-start gap-5 ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#EDE9FE", color: "#7C3AED" }}>
                    {week.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-base mb-2" style={{ color: "#1F2937" }}>{week.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: "#6B7280" }}>{week.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Value Stack */}
        <Reveal delay={300}>
          <div className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-center mb-8" style={{ color: "#1F2937" }}>What You Walk Away With</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deliverables.map((d, i) => (
                <div key={i} className="bg-white rounded-xl p-4 flex items-center justify-between" style={{ border: "1px solid #E5E7EB" }}>
                  <div className="flex items-center gap-3">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#7C3AED" }} />
                    <span className="text-sm" style={{ color: "#374151" }}>{d.title}</span>
                  </div>
                  <span className="text-sm font-semibold flex-shrink-0 ml-2" style={{ color: "#9CA3AF" }}>{d.value}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <p className="text-lg font-bold" style={{ color: "#1F2937" }}>
                Total Value: <span className="line-through" style={{ color: "#9CA3AF" }}>£5,329</span>
              </p>
            </div>
          </div>
        </Reveal>

        {/* Pricing */}
        <Reveal delay={350}>
          <div className="rounded-2xl p-6 md:p-10 mb-16 text-center" style={{ backgroundColor: "#F8F7FF" }}>
            <h2 className="text-xl md:text-2xl font-bold mb-2" style={{ color: "#1F2937" }}>Investment: £1,497</h2>
            <p className="text-sm mb-4" style={{ color: "#6B7280" }}>Or 3 monthly payments of £499</p>
            <p className="text-sm mb-6" style={{ color: "#9CA3AF" }}>That's less than hiring a freelancer for ONE of these deliverables</p>
            <button onClick={scrollToForm} className="font-bold text-base px-10 py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]" style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
              Apply Now — Limited Spots →
            </button>
          </div>
        </Reveal>

        {/* Testimonials */}
        <Reveal delay={400}>
          <div className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-center mb-8" style={{ color: "#1F2937" }}>What Our Clients Say</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-xl p-5" style={{ border: "1px solid #E5E7EB" }}>
                  <p className="text-sm italic mb-3 leading-relaxed" style={{ color: "#374151" }}>"{t.text}"</p>
                  <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>{t.name}</p>
                  <p className="text-xs" style={{ color: "#9CA3AF" }}>{t.role}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* FAQ */}
        <Reveal delay={450}>
          <div className="mb-16">
            <h2 className="text-xl font-bold text-center mb-6" style={{ color: "#1F2937" }}>Frequently Asked Questions</h2>
            <div className="max-w-2xl mx-auto">
              <FAQ question="How much time do I need to commit?" answer="2-3 hours per week for 4 weeks. This includes one 60-minute strategy call per week plus implementation time between calls. We've designed the programme to work alongside your existing schedule." />
              <FAQ question="What if I don't have a product idea yet?" answer="That's exactly what Week 1 is for. We audit your content, audience, and skills to identify the perfect product opportunity for you. Most clients come in without a clear idea and leave with a fully built product." />
              <FAQ question="Can I pay in instalments?" answer="Yes — you can choose to pay 3 monthly payments of £499 instead of the full £1,497 upfront." />
              <FAQ question="What if I'm not happy with the results?" answer="We offer a satisfaction guarantee. If you complete all 4 weeks, implement what we build together, and don't see meaningful progress toward monetisation, we'll work with you until you do." />
              <FAQ question="How is this different from the course?" answer="The course teaches you. This BUILDS it with you. You get 1-on-1 calls, custom-built assets, and hands-on support throughout the entire process. Think of it as hiring a consultant who does the work alongside you." />
              <FAQ question="Do I need tech skills?" answer="No — we handle the technical setup together. If you can use a computer and follow simple instructions, you have all the skills you need." />
            </div>
          </div>
        </Reveal>

        {/* Application Form */}
        <div ref={formRef}>
          <Reveal delay={500}>
            <div className="mb-16">
              {submitted ? (
                <div className="bg-white rounded-2xl p-8 md:p-12 text-center" style={{ border: "1px solid #E5E7EB" }}>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#DCFCE7" }}>
                    <Check className="w-8 h-8" style={{ color: "#16A34A" }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>Application Received!</h3>
                  <p className="text-sm leading-relaxed max-w-md mx-auto" style={{ color: "#6B7280" }}>
                    We review every application personally. Check your email for next steps. If you're a good fit, we'll send you a link to book your strategy call within 24 hours.
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-6 md:p-10" style={{ border: "1px solid #E5E7EB" }}>
                  <h2 className="text-xl md:text-2xl font-bold text-center mb-2" style={{ color: "#1F2937" }}>Apply for Your Done-With-You Spot</h2>
                  <p className="text-sm text-center mb-8" style={{ color: "#6B7280" }}>Answer a few quick questions so we can make sure this is the right fit for you. We'll review your application within 24 hours.</p>

                  <form onSubmit={handleSubmit} className="space-y-5 max-w-lg mx-auto">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>Full name</label>
                      <input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Your full name" className="w-full h-12 px-4 rounded-xl text-base bg-white outline-none focus:ring-2 focus:ring-purple-300" style={{ border: "1px solid #E5E7EB", color: "#1F2937" }} />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>Email</label>
                      <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="your@email.com" className="w-full h-12 px-4 rounded-xl text-base bg-white outline-none focus:ring-2 focus:ring-purple-300" style={{ border: "1px solid #E5E7EB", color: "#1F2937" }} />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>What's your primary content platform?</label>
                      <select value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))} className={selectClass} style={selectStyle}>
                        <option value="">Select a platform</option>
                        {["Instagram", "YouTube", "TikTok", "LinkedIn", "Podcast", "Blog", "Other"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>How large is your current audience?</label>
                      <select value={form.audience_size} onChange={e => setForm(p => ({ ...p, audience_size: e.target.value }))} className={selectClass} style={selectStyle}>
                        <option value="">Select audience size</option>
                        {["Under 1K", "1K-5K", "5K-20K", "20K-50K", "50K+"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>What's your current monthly revenue from content?</label>
                      <select value={form.monthly_revenue} onChange={e => setForm(p => ({ ...p, monthly_revenue: e.target.value }))} className={selectClass} style={selectStyle}>
                        <option value="">Select revenue range</option>
                        {["£0", "Under £500", "£500-£2K", "£2K-£5K", "£5K+"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>What's your biggest challenge right now?</label>
                      <textarea value={form.biggest_challenge} onChange={e => setForm(p => ({ ...p, biggest_challenge: e.target.value }))} placeholder="Be specific — this helps us understand how to help you best..." rows={3} className="w-full px-4 py-3 rounded-xl text-base bg-white outline-none resize-none focus:ring-2 focus:ring-purple-300" style={{ border: "1px solid #E5E7EB", color: "#1F2937" }} />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>Why do you want to work with us specifically?</label>
                      <textarea value={form.why_work_with_us} onChange={e => setForm(p => ({ ...p, why_work_with_us: e.target.value }))} placeholder="What attracted you to this programme?" rows={3} className="w-full px-4 py-3 rounded-xl text-base bg-white outline-none resize-none focus:ring-2 focus:ring-purple-300" style={{ border: "1px solid #E5E7EB", color: "#1F2937" }} />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>Are you ready to invest £1,497 (or 3x£499)?</label>
                      <select value={form.ready_to_invest} onChange={e => setForm(p => ({ ...p, ready_to_invest: e.target.value }))} className={selectClass} style={selectStyle}>
                        <option value="">Select an option</option>
                        {["Yes", "Yes if I can do payments", "I need to think about it"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: "#1F2937" }}>How soon do you want to start?</label>
                      <select value={form.how_soon} onChange={e => setForm(p => ({ ...p, how_soon: e.target.value }))} className={selectClass} style={selectStyle}>
                        <option value="">Select timeline</option>
                        {["Immediately", "Within 2 weeks", "Within a month", "Not sure"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>

                    <button type="submit" disabled={submitting} className="w-full font-bold text-base py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]" style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
                      {submitting ? "Submitting..." : "Submit My Application →"}
                    </button>
                    <p className="text-xs text-center" style={{ color: "#9CA3AF" }}>We review every application personally. You'll hear back within 24 hours.</p>
                  </form>
                </div>
              )}
            </div>
          </Reveal>
        </div>

        {/* Footer */}
        <footer className="py-8" style={{ borderTop: "1px solid #E5E7EB" }}>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
            <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
